import { addDoc, collection } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../../firebase";
import useImageUpload from "../../hooks/useImageUpload";
import { initialEventData } from "../../utilities/reusableItems";
import GoogleMaps from "../GoogleMaps";
import ImageUploader from "../ImageUploader";
import LexicalEditor from "../text-editor/MyLexicalEditor";
import TimeSelect from "../TimeSelect";
import BigButton from "./BigButton";
import Spinner from "../Spinner";

const CreateEvent = ({ user, copiedEvent }) => {
	const [eventData, setEventData] = useState(
		copiedEvent
			? {
					...copiedEvent,
					image: null,
					title: copiedEvent.title + " (Copy)",
				}
			: initialEventData,
	);

	const [saving, setSaving] = useState(false);
	const navigate = useNavigate();

	const handleImageFromUploader = (file) => {
		setEventData((prev) => ({ ...prev, image: file }));
	};

	const handleChange = (e) => {
		setEventData({ ...eventData, [e.target.name]: e.target.value });
	};

	const { resizeImage } = useImageUpload();

	const handleSubmit = async (e) => {
		setSaving(true);
		e.preventDefault();

		try {
			let imageUrl = eventData.imageUrl || "";

			if (eventData.image) {
				const resizedImage = await resizeImage(eventData.image);
				const imageRef = ref(storage, `events/${user.uid}/${eventData.image.name}`);
				await uploadBytes(imageRef, resizedImage);
				imageUrl = await getDownloadURL(imageRef);
			}

			await addDoc(collection(db, "events"), {
				title: eventData.title,
				date: eventData.date,
				startTime: eventData.startTime,
				endTime: eventData.endTime,
				imageUrl,
				createdBy: user.displayName || "Anonymous",
				creatorPhoto: user.photoURL || "/images/placeholder.png",
				userId: user.uid,
				name: eventData.name,
				address: eventData.address,
				lat: eventData.lat,
				lng: eventData.lng,
				details: eventData.details,
				placeId: eventData.placeId,
				staticMapUrl: eventData.staticMapUrl,
				eventStatus: "active",
				attending: [user.uid],
			});

			alert("Event created successfully!");
			setEventData(initialEventData);
			navigate("/events");
		} catch (error) {
			console.error("Error adding event: ", error);
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Container className="mt-3">
				<BigButton title="Back to events" url="/events" icon="bi-arrow-left-circle" />
				<h2>Create Event</h2>
				<form onSubmit={handleSubmit} className="p-2 p-sm-4 border rounded bg-secondary">
					<div className="mb-3">
						<ImageUploader
							label="Event Image"
							onImageChange={handleImageFromUploader}
							savedImage={eventData.imageUrl}
						/>
					</div>
					<div className="mb-3">
						<label className="form-label">Event Title</label>
						<input
							type="text"
							name="title"
							className="form-control bg-dark text-light"
							value={eventData.title}
							onChange={handleChange}
							required
							placeholder="Enter a title for your event"
						/>
					</div>
					<div className="mb-3">
						<label className="form-label">
							<i className="bi bi-calendar me-2" />
							Date
						</label>
						<input
							type="date"
							name="date"
							className="form-control bg-dark text-light"
							value={eventData.date}
							onChange={handleChange}
							required
						/>
					</div>

					{/* Start & End Time */}
					<div className="mb-3">
						<label className="form-label">
							<i className="bi bi-clock me-2" />
							Time
						</label>
						<div className="d-flex align-items-center gap-2">
							<TimeSelect name="startTime" value={eventData.startTime} onChange={handleChange} inline />
							<span className="text-light fw-semibold flex-shrink-0">to</span>
							<TimeSelect name="endTime" value={eventData.endTime} onChange={handleChange} inline required={false} />
						</div>
					</div>

					<GoogleMaps eventData={eventData} handleChange={handleChange} setEventData={setEventData} />

					<div className="my-3">
						<label className="form-label">Event Details</label>
						<LexicalEditor
							initialValue={eventData.details}
							onChange={(val) => setEventData({ ...eventData, details: val })}
						/>
					</div>
					<button type="submit" className="btn my-btn" disabled={saving}>
						{saving ? <Spinner size="sm" /> : "Create Event"}
					</button>
				</form>
			</Container>
		</>
	);
};

export default CreateEvent;
